/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { AuthDialog } from '../components/AuthDialog/AuthDialog';

type AuthMode = 'login' | 'signup' | 'forgot-password' | 'otp-verification';

interface AuthDialogContextType {
  showAuthDialog: (mode?: AuthMode, onSuccess?: () => void) => void;
  hideAuthDialog: () => void;
  isAuthDialogOpen: boolean;
}

const AuthDialogContext = createContext<AuthDialogContextType | undefined>(undefined);

export function useAuthDialog() {
  const context = useContext(AuthDialogContext);
  if (context === undefined) {
    throw new Error('useAuthDialog must be used within an AuthDialogProvider');
  }
  return context;
}

interface AuthDialogProviderProps {
  children: ReactNode;
}

export function AuthDialogProvider({ children }: AuthDialogProviderProps) {
  const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false);
  const [authMode, setAuthMode] = useState<AuthMode>('login');
  const [onSuccessCallback, setOnSuccessCallback] = useState<(() => void) | undefined>(undefined);

  const showAuthDialog = useCallback((mode: AuthMode = 'login', onSuccess?: () => void) => {
    setAuthMode(mode);
    setOnSuccessCallback(() => onSuccess);
    setIsAuthDialogOpen(true);
  }, []);

  const hideAuthDialog = useCallback(() => {
    setIsAuthDialogOpen(false);
    setOnSuccessCallback(undefined);
  }, []);

  const handleAuthSuccess = () => {
    if (onSuccessCallback) {
      onSuccessCallback();
    }
    hideAuthDialog();
  };

  const value = {
    showAuthDialog,
    hideAuthDialog,
    isAuthDialogOpen,
  };

  return (
    <AuthDialogContext.Provider value={value}>
      {children}
      <AuthDialog
        isOpen={isAuthDialogOpen}
        onClose={hideAuthDialog}
        onSuccess={handleAuthSuccess}
        initialMode={authMode}
      />
    </AuthDialogContext.Provider>
  );
}
